/* eslint-disable react/no-danger */
import {
  Avatar,
  BasketIcon,
  Box,
  Card,
  DoubleDownArrowIcon,
  GKButton,
  Grid2,
  Typography,
  UserIcon,
} from "components";
import { useFormContext, useWatch } from "react-hook-form";
import { useAppSelector } from "store/store";
import { WebsiteDetails } from "../types";

const HeroSection = () => {
  const form = useFormContext<WebsiteDetails>();

  const croppedAdvisorImage = useAppSelector(
    (state) => state.myWebsite.croppedAdvisorImage,
  );

  const [heroSection, myBranding] = useWatch({
    control: form.control,
    name: ["websiteSection.heroSection", "websiteSection.myBranding"],
  });

  const focusedField = useAppSelector(
    (state) => state?.myWebsite?.focusedField,
  );

  return (
    <Grid2
      container
      width={"100%"}
      spacing={{ xs: 5, md: 10 }}
      maxWidth={{ xs: "100vw", md: "1192px" }}
    >
      <Grid2
        size={{ xs: 12, md: 6 }}
        display={"flex"}
        alignContent={"center"}
        container
        spacing={2}
      >
        <Grid2
          size={12}
          display={"flex"}
          alignItems={"center"}
          justifyContent={{ xs: "center", md: "flex-start" }}
        >
          <img
            src={
              `/RAWebsite/symbol-${heroSection?.heroSectionLogo}.png` ||
              "/heroLogo.svg"
            }
            alt=""
            width={"auto"}
            height={"auto"}
            style={{ maxHeight: "34px" }}
          />
        </Grid2>
        <Grid2
          size={12}
          display={"flex"}
          alignItems={"center"}
          justifyContent={{ xs: "center", md: "flex-start" }}
        >
          <Box
            fontSize={`clamp(1.5rem, 0.875rem + 2vw, 1.8rem)`}
            color={myBranding?.colorPalette.primary}
            bgcolor={`${myBranding?.colorPalette.primary}22`}
            fontWeight={600}
            py={1}
            px={2}
            borderRadius={2}
            textAlign={{ xs: "center", md: "left" }}
            border={
              focusedField === "websiteSection.heroSection.companyMetaName"
                ? "2px solid #007bff"
                : undefined
            }
            sx={{
              "& .ck p span": {
                color: `${myBranding?.colorPalette.primary} !important`,
                background: "transparent !important",
                fontWeight: 700,
                margin: "0 !important",
              },
              "& .ck p": {
                color: `${myBranding?.colorPalette.primary} !important`,
                fontWeight: 700,
                margin: "0 !important",
              },
            }}
          >
            <div
              className="ck"
              dangerouslySetInnerHTML={{ __html: heroSection?.companyMetaName }}
            />
          </Box>
        </Grid2>
        <Grid2 size={12}>
          <Box
            fontSize={`clamp(1.25rem, 0.4688rem + 2.5vw, 1.5rem)`}
            fontWeight={600}
            textAlign={{ xs: "center", md: "left" }}
            borderRadius={
              focusedField === "websiteSection.heroSection.tagLine"
                ? 2
                : undefined
            }
            border={
              focusedField === "websiteSection.heroSection.tagLine"
                ? "2px solid #007bff"
                : undefined
            }
            sx={{
              "& .ck p": {
                textAlign: {
                  xs: "center !important",
                  md: "left !important",
                },
                margin: "0 !important",
              },
              "& .ck h2": {
                textAlign: {
                  xs: "center !important",
                  md: "left !important",
                },
                margin: "0 !important",
              },
            }}
          >
            <div
              className="ck"
              dangerouslySetInnerHTML={{ __html: heroSection?.tagLine }}
            />
          </Box>
        </Grid2>
        <Grid2 size={12}>
          <Box
            fontSize={12}
            borderRadius={
              focusedField === "websiteSection.heroSection.tagLineBody"
                ? 2
                : undefined
            }
            border={
              focusedField === "websiteSection.heroSection.tagLineBody"
                ? "2px solid #007bff"
                : undefined
            }
            sx={{
              "& .ck p": {
                textAlign: {
                  xs: "center",
                  md: "left",
                },
                fontSize: "14px !important",
                bgcolor: "transparent !important",
                margin: "0 !important",
              },
              "& .ck": {
                fontSize: "14px !important",
                bgcolor: "transparent !important",
              },
              "& .ck span": {
                bgcolor: "transparent !important",
              },
            }}
          >
            <div
              className="ck"
              dangerouslySetInnerHTML={{ __html: heroSection?.tagLineBody }}
            />
          </Box>
        </Grid2>
        <Grid2
          size={12}
          display={"flex"}
          flexWrap={"wrap"}
          gap={1}
          alignItems={"center"}
          justifyContent={{ xs: "center", md: "flex-start" }}
        >
          <GKButton
            variant="contained"
            sx={{ width: { xs: "100%", md: "auto" } }}
            startIcon={<BasketIcon />}
            endIcon={<DoubleDownArrowIcon />}
          >
            Explore Baskets
          </GKButton>
        </Grid2>
      </Grid2>
      <Grid2
        size={{ xs: 12, md: 6 }}
        container
        position={"relative"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Grid2 size={12} textAlign={"center"}>
          <img
            src={croppedAdvisorImage || (heroSection?.advisorPhoto as string)}
            alt=""
            width={"100%"}
            style={{ maxHeight: "300px" }}
          />
        </Grid2>
        <Card
          sx={{
            position: "absolute",
            bottom: 16,
            width: "70%",
            background: "#ffffffc5",
            border:
              focusedField === "websiteSection.heroSection.advisorName"
                ? "2px solid #007bff"
                : "1px solid #fff",
            backdropFilter: "blur(10px)",
            boxShadow: " 0 5px 10px #00000022",
            p: { xs: 1, md: 2, lg: 3 },
          }}
        >
          <Grid2 container>
            <Grid2 size={2} display={"flex"} alignItems={"center"}>
              <Avatar
                sx={{
                  width: { xs: 24, md: 40 },
                  height: { xs: 24, md: 40 },
                  bgcolor: "var(--primary-50)",
                }}
              >
                <UserIcon width={20} height={20} color="var(--primary-900)" />
              </Avatar>
            </Grid2>
            <Grid2 size={10}>
              <Typography fontSize={18} fontWeight={600}>
                {heroSection?.advisorName}
              </Typography>
              <Typography fontSize={12} fontWeight={40}>
                Research Analyst
              </Typography>
            </Grid2>
          </Grid2>
        </Card>
      </Grid2>
    </Grid2>
  );
};

export default HeroSection;
